// A landing's approval on the phone (Poulpe-625), as the approved prototype
// draws it (mobile/ApprovalScreen.tsx, Poulpe-622). First comes the exact
// subject, whole: the commit, the tree, the proposals, the policy version, the
// evidence and the digest that the confirmation names. Then why it needs the
// person, and "See what it changes" (optional). Only after them, at the end of
// the body that scrolls, comes "Approve with passkey".
//
// Approving is the desktop's own path: store.approveLanding, as the Board's
// approval strip calls it (board/ApprovalStrip.tsx). The store checks the
// subject's digest (queue/subject.ts), confirms exactly that digest with a
// passkey, then sends `approve_landing` with that subject. The request is the
// same on web, on the Mac and on the iPhone. On the iPhone, the shell shows
// the system's passkey sheet, with Face ID, through the bridge every shell
// speaks (api/shellPasskeys.ts). The phone says "approved" only once the
// server has accepted. A cancelled or refused passkey approves nothing.
// When the subject changed meanwhile, the server does not accept the
// approval, and the new subject shows here to be read again.
//
// A batch that stopped and holds its proposals shows here too, with why. What
// to do with it is the Board's.
import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { ApprovalAsked, Landing, Proposal } from '../api/types'
import { needsServer } from '../components/needsServer'
import { useMounted } from '../components/layout'
import { toast } from '../components/toasts'
import { confirmsActions } from '../people/actions'
import { partsNamed, problemText, reasonText, short } from '../queue/words'
import type { ApprovalOutcome, BoardStore } from '../store/store'
import { useBoardState, useStore } from '../store/context'
import { approvalIssues, approvalOf, carriedIssues, heldWhy } from './needs'
import { IssueName, Note, Screen, TopBar } from './shared'

/**
 * What the screen says once an approval was sent: approved, not accepted, or
 * why nothing was approved; with the digest it was sent for, and why it
 * failed, if it did.
 */
interface Said {
  tone: 'ok' | 'warn' | 'error'
  text: string
  digest: string
  problem: Extract<ApprovalOutcome, { ok: false }>['problem'] | null
}

/** The phone's words for an approval's result. The desktop's (queue/words.ts, approvalSaid) say "below" and "browser". */
export function phoneSaid(outcome: ApprovalOutcome, shown: ApprovalAsked): Said {
  const said = (tone: Said['tone'], text: string): Said => ({ tone, text, digest: shown.digest, problem: outcome.ok ? null : outcome.problem })
  if (!outcome.ok) {
    if (outcome.problem === 'unsupported') return said('error', 'Passkeys are not available here, so nothing was approved. Approve from a device that offers them.')
    if (outcome.problem === 'mismatch') return said('error', 'Nothing was sent: the subject changed since it was shown. Wait for the new subject to show here, then read it again.')
    return said('error', problemText(outcome))
  }
  if (outcome.accepted) {
    const after = outcome.landing.state === 'deferred' ? 'It waits behind work already queued.' : 'The landing goes on.'
    return said('ok', `Approved commit ${short(shown.subject.commit)}. ${after}`)
  }
  if (outcome.landing.state === 'finished') return said('warn', 'Not accepted: the subject changed before your approval reached the landing, which has ended.')
  return said('warn', 'Not accepted: the subject changed before your approval reached the landing. Read the subject above again: it is the one asked now.')
}

/**
 * The approvals on their way, by landing. One survives the screen that sent
 * it: back on the screen, the person sees it still going, and its result;
 * it is never sent twice.
 */
const going = new Map<string, { promise: Promise<ApprovalOutcome>; shown: ApprovalAsked }>()

/** How many screens show each landing's approval: one that shows it says its result there, not in a toast. */
const watching = new Map<string, number>()

/** Approve a landing once at a time: while an approval of it is on its way, another joins it. */
function approveOnce(store: BoardStore, landingId: string, shown: ApprovalAsked) {
  const sent = going.get(landingId)
  if (sent) return sent
  const promise = store.approveLanding(landingId, shown).finally(() => going.delete(landingId))
  const fresh = { promise, shown }
  going.set(landingId, fresh)
  return fresh
}

/** One approval's screen, drawn afresh for each landing: what it said of one is not said of another. */
export function ApprovalScreen() {
  const { id = '' } = useParams()
  return <Approval key={id} id={id} />
}

function Approval({ id }: { id: string }) {
  const store = useStore()
  const mounted = useMounted()
  const { landings, held, dismissed, projects, proposals, issues, offline, me } = useBoardState()
  const [busy, setBusy] = useState(() => going.has(id))
  const [kept, setSaid] = useState<Said | null>(null)
  const stopped = dismissed.has(id) ? undefined : held.get(id)
  const landing = landings.get(id)
  const asked = landing ? approvalOf(landing) : null
  const top = <TopBar title="Approval" />
  // An approval taken, of a subject the landing no longer asks: it asks again for another, to read afresh.
  const said = kept?.tone === 'ok' && asked && asked.digest !== kept.digest ? null : kept

  /** Wait for an approval on its way, and say how it fared: here, or in a toast once the screen is gone. */
  const follow = async ({ promise, shown }: { promise: Promise<ApprovalOutcome>; shown: ApprovalAsked }) => {
    setBusy(true)
    setSaid(null)
    const result = phoneSaid(await promise, shown)
    if (!mounted.current) {
      if (watching.get(id)) return
      toast(result.text, result.tone === 'error' ? 'error' : 'info')
      return
    }
    setSaid(result)
    setBusy(false)
  }
  // Back on the screen while an approval sent from it is still on its way: follow it.
  // Once, as the screen opens; `follow` reads only what does not change for it.
  useEffect(() => {
    watching.set(id, (watching.get(id) ?? 0) + 1)
    const sent = going.get(id)
    if (sent) void follow(sent)
    return () => {
      const left = (watching.get(id) ?? 1) - 1
      if (left > 0) watching.set(id, left)
      else watching.delete(id)
    }
  }, [])

  const last = said && <Note tone={said.tone === 'ok' ? 'm-ok' : said.tone === 'error' ? 'question-error' : 'question-changed'} role={said.tone === 'error' ? 'alert' : 'status'}>{said.text}</Note>
  // Approved: the landing asks no more, and the screen says so until the person leaves it.
  if (said?.tone === 'ok') {
    return (
      <Screen top={top}>
        {last}
        <Link className="button m-target" to="/">Back to Needs you</Link>
      </Screen>
    )
  }
  if (stopped) return <Stopped landing={stopped} said={last} />
  if (!landing || !asked) {
    return <Screen top={top}>{last}<Note>This approval is no longer asked: the landing went on or ended.</Note></Screen>
  }

  const approve = (shown: ApprovalAsked) => {
    if (busy || going.has(landing.id)) return
    void follow(approveOnce(store, landing.id, shown))
  }

  const project = projects.get(landing.project_id)
  const carried = approvalIssues(asked, proposals, issues)
  const { subject } = asked
  const passkey = confirmsActions(me)
  const failed = said !== null && said.tone === 'error'
  // The subject shown is not the one the server asks: approving it again would send nothing again.
  const outdated = said?.problem === 'mismatch' && said.digest === asked.digest
  return (
    <Screen top={top}>
      <section className="m-context" aria-label="What lands">
        <div className="request-top">
          <span className="kind-chip">Approval</span>
          {project && <span className="request-project">{project.name}</span>}
        </div>
        {carried.length > 0 && (
          <ul className="line-list" aria-label="Issues it carries">
            {carried.map((issue) => <li key={issue.id}><IssueName issue={issue} /></li>)}
          </ul>
        )}
      </section>
      {/* The desktop's approval (queue/Approval.tsx), in its own classes; its button comes last, below. */}
      <div className="approval request-card" role="group" aria-label="Approval">
        <p className="approval-ask">Main moves only once you approve exactly this:</p>
        <section aria-label="The exact subject">
          <dl className="approval-subject">
            <dt>Commit</dt><dd><code>{subject.commit}</code></dd>
            <dt>Tree</dt><dd><code>{subject.tree}</code></dd>
            <dt>Proposals</dt>
            <dd>{subject.proposals.map((p) => <ProposalLine key={p} id={p} proposal={proposals.get(p)} />)}</dd>
            <dt>Policy</dt><dd>version {subject.policy_version}</dd>
            <dt>Evidence</dt>
            <dd>{subject.evidence.length === 0 ? <span className="muted">none</span> : subject.evidence.map((e) => <code key={e} className="m-block" title="Receipt">{e}</code>)}</dd>
            <dt>Digest</dt><dd><code>{asked.digest}</code></dd>
          </dl>
        </section>
        {asked.reasons.length > 0 && (
          <section aria-label="Why it needs you">
            <ul className="approval-reasons">{asked.reasons.map((reason, i) => <li key={i}>{reasonText(reason)}</li>)}</ul>
          </section>
        )}
        {asked.stale && (
          <p className="approval-stale" role="status">
            An approval of another subject was refused: its {partsNamed(asked.stale.differs)} differed. This is the subject asked now; read it again.
          </p>
        )}
      </div>
      {subject.proposals.length > 0 && (
        <Link className="button m-target" to={`/approval/${encodeURIComponent(landing.id)}/changes`}>See what it changes</Link>
      )}
      {busy && <Note>{passkey ? 'Checking the passkey…' : 'Approving…'}</Note>}
      {last}
      {/* The action comes after the whole subject, in the body that scrolls: it cannot be reached before the subject is read. */}
      <div className="m-action">
        {offline && <p className="form-hint">Approving needs a connection: the server must check the passkey against this exact subject.</p>}
        <button type="button" className="button button-primary m-target" disabled={busy || offline || outdated} {...needsServer(offline)} onClick={() => void approve(asked)}>
          {busy ? 'Approving…' : !passkey ? 'Approve' : failed && !outdated ? 'Try again with passkey' : 'Approve with passkey'}
        </button>
      </div>
    </Screen>
  )
}

/** A proposal of the subject: its id, whole, and the keys of the issues it carries. */
function ProposalLine({ id, proposal }: { id: string; proposal: Proposal | undefined }) {
  const { proposals, issues } = useBoardState()
  const keys = proposal ? carriedIssues([id], proposals, issues).map((issue) => issue.key) : []
  return <span className="m-block"><code>{id}</code>{keys.map((key) => ` · ${key}`)}</span>
}

/** A batch that stopped and holds its proposals: which, and why. */
function Stopped({ landing, said }: { landing: Landing; said: ReactNode }) {
  const { projects, proposals, issues } = useBoardState()
  const project = projects.get(landing.project_id)
  const carried = carriedIssues(landing.proposals, proposals, issues)
  return (
    <Screen top={<TopBar title="Stopped" />}>
      {said}
      <section className="m-context" aria-label="What it holds">
        <div className="request-top">
          <span className="kind-chip">Stopped</span>
          {project && <span className="request-project">{project.name}</span>}
        </div>
        {carried.length > 0 && (
          <ul className="line-list" aria-label="Issues it holds">
            {carried.map((issue) => <li key={issue.id}><IssueName issue={issue} /></li>)}
          </ul>
        )}
      </section>
      <Note tone="question-error">{heldWhy(landing, proposals, issues)}</Note>
      <Note tone="question-changed">The merge stopped and holds its proposals. See to it on a computer, from {project ? `${project.name}'s` : 'the project’s'} Board.</Note>
    </Screen>
  )
}
